import { useRef, useState } from 'react'

/**
 * Whether the record under an open editor was rewritten by somebody else.
 *
 * The settings editors are remounted on a version change, which throws away any
 * draft in them. That is the right trade — that endpoint takes no version, so
 * the server cannot reject a stale write, and keeping the draft would mean
 * quietly overwriting a colleague's decision. But *silently* discarding what
 * somebody typed reads as the console losing their work, so the discard is
 * announced.
 *
 * The distinction that makes it work: a bump this tab caused is a save, a bump
 * it did not cause is somebody else's. The refetched record cannot tell them
 * apart, so the write counter does.
 *
 * `version` is 0 while the record has not arrived. Arming on that first
 * 0 → n transition is the defect this hook shipped with until 2026-10-17: the
 * query is pending on the first render of a freshly opened page, so **every**
 * section announced a foreign write the moment its data loaded, to an operator
 * who had not typed anything and whose colleague had not touched it. A warning
 * that fires on every visit is one nobody reads on the visit that matters, and
 * this one claims that work was discarded.
 *
 * Nothing was discarded before a record existed, so a version arriving where
 * there was none is a load, not a write. Stored records start at version 1
 * (`domain.NewMeta`), and a key the collection does not hold stays at 0 and
 * never arms — which is correct for the same reason.
 */
export function useForeignWrite(version: number, writes: number): boolean {
  const seen = useRef({ version, writes })
  const [foreign, setForeign] = useState(false)

  if (seen.current.version !== version || seen.current.writes !== writes) {
    const ours = seen.current.writes !== writes
    const firstLoad = seen.current.version === 0
    seen.current = { version, writes }
    if (!ours && !firstLoad) setForeign(true)
    else if (foreign) setForeign(false)
  }
  return foreign
}
